import React, { Component } from 'react';
import moment from 'moment';
import { string, bool, func } from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';
import {
  Form,
  FieldTextInput,
  PrimaryButton,
  IconCheckmark,
  LocationAutocompleteInputField,
  FieldDateRangeInput,
} from '../../components';
import * as validators from '../../util/validators';
import { propTypes } from '../../util/types';
import ReCAPTCHA from 'react-google-recaptcha';
import config from '../../config';
import QueryTypeSelectField from './QueryTypeSelectField';
import { queryTypeContactUsConfig } from '../../marketplace-custom-config';
import { OnChange } from 'react-final-form-listeners';

import css from './ContactUsForm.css';

const identity = v => v;

const OPTION_SHOW_QUERY_FORM = 'helpMeFindWhatINeed';

class ContactUsFormComponent extends Component {
  constructor(props) {
    super(props);
    this.submittedValues = {};
    this.reCaptchaRef = React.createRef();
    this.queryTypeRef = React.createRef();

    this.state = {
      captchaToken: null,
      isVisibleQueryForm: false,
      focusedInput: null,
    };
  }

  onChangeCaptcha = value => {
    this.setState({
      captchaToken: value,
    });
  };

  onFocusedInputChange = focusedInput => {
    this.setState({ focusedInput });
  };

  render() {
    const setVisibilityQueryForm = value => {
      this.setState({
        isVisibleQueryForm: value === OPTION_SHOW_QUERY_FORM,
      });
    };
    return (
      <FinalForm
        {...this.props}
        onSubmit={e => {
          return this.props
            .onSubmit(e)
            .then(() => {
              if (this.reCaptchaRef && this.reCaptchaRef.current) {
                this.reCaptchaRef.current.reset();
              }

              if (this.queryTypeRef && this.queryTypeRef.current) {
                this.queryTypeRef.current.resetFieldState('queryType');
              }

              this.setState({
                captchaToken: null,
              });
            })
            .catch(() => {
              if (this.reCaptchaRef && this.reCaptchaRef.current) {
                this.reCaptchaRef.current.reset();
              }
              if (this.queryTypeRef && this.queryTypeRef.current) {
                this.queryTypeRef.current.resetFieldState('queryType');
              }
              this.setState({
                captchaToken: null,
              });
            });
        }}
        render={formRenderProps => {
          const {
            rootClassName,
            className,
            handleSubmit,
            submitInProgress,
            sendMessageSuccess,
            sendMessageError,
            invalid,
            form,
            formId,
            intl,
            values,
          } = formRenderProps;

          if (!this.queryTypeRef.current) {
            this.queryTypeRef.current = form;
          }

          const emailLabel = intl.formatMessage({ id: 'ContactUsForm.emailLabel' });
          const emailPlaceholder = intl.formatMessage({ id: 'ContactUsForm.emailPlaceholder' });

          const now = moment();
          const today = now.startOf('day').toDate();
          const tomorrow = now
            .startOf('day')
            .add(1, 'days')
            .toDate();
          const dateFormatOptions = {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          };

          const locationLabel = intl.formatMessage({ id: 'SearchNilForm.locationLabel' });
          const locationPlaceholder = intl.formatMessage({
            id: 'SearchNilForm.locationPlaceholder',
          });

          const dateLabel = intl.formatMessage({ id: 'SearchNilForm.dateLabel' });
          const startDatePlaceholderText = intl.formatDate(today, dateFormatOptions);
          const endDatePlaceholderText = intl.formatDate(tomorrow, dateFormatOptions);

          const contentLabel = intl.formatMessage({ id: 'ContactUsForm.contentLabel' });
          const contentPlaceholder = intl.formatMessage({ id: 'ContactUsForm.contentPlaceholder' });

          const contentQueryLabel = intl.formatMessage({ id: 'SearchNilForm.contentLabel' });
          const contentQueryPlaceholder = intl.formatMessage({
            id: 'SearchNilForm.contentPlaceholder',
          });

          const emailRequired = validators.required(intl.formatMessage({
            id: 'ContactUsForm.emailRequireMessage',
          }));
          const emailValid = validators.emailFormatValid(intl.formatMessage({
            id: 'ContactUsForm.emailInvalid',
          }));

          const classes = classNames(rootClassName || css.root, className);

          const isQueryTypeFormValidated = values => {
            if (values.queryType !== OPTION_SHOW_QUERY_FORM) {
              return true;
            }

            if (
              !values.lookingFor ||
              !values.onDates ||
              !values.location ||
              (!!values.onDates && (!values.onDates.startDate || !values.onDates.endDate)) ||
              (!!values.location && !values.location.search)
            ) {
              return false;
            }

            return true;
          };

          const submitDisabled =
            invalid ||
            submitInProgress ||
            !this.state.captchaToken ||
            !values.email ||
            !values.message ||
            !values.queryType ||
            !isQueryTypeFormValidated(values);

          return (
            <Form
              className={classes}
              onSubmit={e => {
                this.submittedValues = values;
                handleSubmit(e);
              }}
            >
              <QueryTypeSelectField
                id="queryType"
                name="queryType"
                categories={queryTypeContactUsConfig}
                intl={intl}
              />

              <OnChange name="queryType">
                {value => {
                  setVisibilityQueryForm(value);
                }}
              </OnChange>

              <FieldTextInput
                className={css.emailField}
                type="text"
                id={formId ? `${formId}.email` : 'email'}
                name="email"
                label={emailLabel}
                placeholder={emailPlaceholder}
                validate={validators.composeValidators(emailRequired, emailValid)}
              />

              <div className={classNames({ [css.hidden]: !this.state.isVisibleQueryForm })}>
                <FieldTextInput
                  className={css.textfield}
                  type="text"
                  id={formId ? `${formId}.lookingFor ` : 'lookingFor'}
                  name="lookingFor"
                  label={contentQueryLabel}
                  placeholder={contentQueryPlaceholder}
                />

                <div className={css.bookingDatesWrapper}>
                  <label>{dateLabel}</label>
                  <FieldDateRangeInput
                    className={css.bookingDates}
                    name="onDates"
                    startDateId={`bookingStartDate`}
                    startDatePlaceholderText={startDatePlaceholderText}
                    endDateId={`bookingEndDate`}
                    endDatePlaceholderText={endDatePlaceholderText}
                    focusedInput={this.state.focusedInput}
                    onFocusedInputChange={this.onFocusedInputChange}
                    format={identity}
                    unitType="line-item/night"
                  />
                </div>

                <LocationAutocompleteInputField
                  className={css.locationAddress}
                  inputClassName={css.locationAutocompleteInput}
                  iconClassName={css.locationAutocompleteInputIcon}
                  predictionsClassName={css.predictionsRoot}
                  validClassName={css.validLocation}
                  name={'location'}
                  label={locationLabel}
                  placeholder={locationPlaceholder}
                  useDefaultPredictions={false}
                  format={identity}
                  valueFromForm={values.location}
                  autoComplete={'nope'}
                />
              </div>

              <FieldTextInput
                className={css.textarea}
                type="textarea"
                id={formId ? `${formId}.message` : 'message'}
                name="message"
                label={contentLabel}
                placeholder={contentPlaceholder}
              />

              <div className={css.field}>
                <ReCAPTCHA
                  ref={this.reCaptchaRef}
                  sitekey={config.reCaptchaV2Key}
                  onChange={this.onChangeCaptcha}
                />
              </div>

              <div className={css.submitContainer}>
                <FormSpy subscription={{ pristine: true }}>
                  {props => {
                    return props.pristine ? (
                      <div>
                        <div className={css.errorContainer}>
                          {sendMessageError ? (
                            <p className={css.error}>
                              <FormattedMessage id="ContactUsForm.sendFailed" />
                            </p>
                          ) : null}
                        </div>
                        {sendMessageSuccess ? (
                          <p className={css.sentSuccess}>
                            <IconCheckmark className={css.checkmark} />
                            &nbsp;&nbsp;&nbsp;
                            <FormattedMessage id="ContactUsForm.sendSuccess" />
                          </p>
                        ) : null}
                      </div>
                    ) : null;
                  }}
                </FormSpy>
                <PrimaryButton
                  rootClassName={css.submitButton}
                  inProgress={submitInProgress}
                  disabled={submitDisabled}
                >
                  <FormattedMessage id="ContactUsForm.sendMessage" />
                </PrimaryButton>
              </div>
            </Form>
          );
        }}
      />
    );
  }
}

ContactUsFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inProgress: false,
  messagePlaceholder: null,
  sendMessageError: null,
};

ContactUsFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  inProgress: bool,

  messagePlaceholder: string,
  onSubmit: func.isRequired,
  sendMessageError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,
};

const ContactUsForm = compose(injectIntl)(ContactUsFormComponent);

ContactUsForm.displayName = 'ContactUsForm';

export default ContactUsForm;
